import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import { DimsUom, VolumeUom, WeightUom } from '@rygen/rygen-ui/model';

import { LineItem } from '@/model';

interface UseLineItemsSummaryResponse {
  handlingUnits: ComputedRef<string>;
  packagingUnits: ComputedRef<string>;
  totalHandlingUnits: ComputedRef<number>;
  maxLength: ComputedRef<number>;
  totalLinear: ComputedRef<number>;
  totalPackagingUnits: ComputedRef<number>;
  totalVolume: ComputedRef<number>;
  totalWeight: ComputedRef<number>;
}

export const useLineItemsSummary = (
  lineItems: MaybeRefOrGetter<LineItem[]>,
): UseLineItemsSummaryResponse => {
  const { t } = useI18n();

  const maxLength = computed(() => {
    return toValue(lineItems).reduce((maxLength, lineItem) => {
      if (!lineItem.linear) {
        return maxLength;
      }

      const linear = DimsUom.convert(
        lineItem.linear,
        lineItem.linearUom,
        DimsUom.FOOT,
      );

      const newMax = Math.max(linear, maxLength);
      return Math.round(newMax);
    }, 0);
  });

  const totalLinear = computed(() => {
    return toValue(lineItems).reduce((totalLinear, lineItem) => {
      if (!lineItem.linear || !lineItem.linearUom) {
        return totalLinear;
      }
      const linear = DimsUom.convert(
        lineItem.linear,
        lineItem.linearUom,
        DimsUom.FOOT,
      );

      return Math.round(totalLinear + linear);
    }, 0);
  });

  const totalWeight = computed(() => {
    return toValue(lineItems).reduce((totalWeight, lineItem) => {
      if (!lineItem.weight || !lineItem.weightUom) {
        return totalWeight;
      }
      const weight = WeightUom.convert(
        lineItem.weight,
        lineItem.weightUom,
        WeightUom.POUND,
      );

      return Math.round(totalWeight + weight);
    }, 0);
  });

  const totalVolume = computed(() => {
    return toValue(lineItems).reduce((totalVolume, lineItem) => {
      if (!lineItem.volume || !lineItem.volumeUom) {
        return totalVolume;
      }

      const volume = VolumeUom.convert(
        lineItem.volume,
        lineItem.volumeUom,
        VolumeUom.CUBIC_FOOT,
      );

      return Math.round(totalVolume + volume);
    }, 0);
  });

  const totalHandlingUnits = computed(() => {
    return toValue(lineItems)
      .map((x) => x.quantity || 0)
      .reduce((sum, curr) => curr + sum, 0);
  });

  const totalPackagingUnits = computed(() => {
    return toValue(lineItems)
      .map((x) => x.packagingQty || 0)
      .reduce((sum, curr) => curr + sum, 0);
  });

  /*
  if it is a single type, show that type, e.g. "3 Pallets", "3 Barrels", etc.
  if there are mixed handling units, show "handling units". e.g.
  "6 Handling Units"
  */
  const handlingUnits = computed(() => {
    const actualLineItems = toValue(lineItems);
    const areAllSameUnit = actualLineItems.every(
      (lineItem) =>
        lineItem?.handlingUnit?.naturalId ===
        actualLineItems[0]?.handlingUnit?.naturalId,
    );
    return areAllSameUnit && !!actualLineItems[0]?.handlingUnit?.name
      ? actualLineItems[0]?.handlingUnit?.name
      : t('lineItems.handlingUnits');
  });

  const packagingUnits = computed(() => {
    const actualLineItems = toValue(lineItems);
    const areAllSameUnit = actualLineItems.every(
      (lineItem) =>
        lineItem?.packagingUnit?.naturalId ===
        actualLineItems[0]?.packagingUnit?.naturalId,
    );
    return areAllSameUnit && !!actualLineItems[0]?.packagingUnit?.name
      ? actualLineItems[0]?.packagingUnit?.name
      : t('lineItems.packagingUnits');
  });

  return {
    handlingUnits,
    packagingUnits,
    totalHandlingUnits,
    maxLength,
    totalLinear,
    totalPackagingUnits,
    totalVolume,
    totalWeight,
  };
};
